import { Copy, RefreshCw, ThumbsUp, ThumbsDown, FileText, Image as ImageIcon } from "lucide-react";
import { type Message } from "@/hooks/useChat";
import { useState } from "react";

interface MessageBubbleProps {
  message: Message;
  isStreaming?: boolean;
}

export function MessageBubble({ message, isStreaming }: MessageBubbleProps) {
  const [showActions, setShowActions] = useState(false);
  const isUser = message.role === "user";

  const renderContent = (text: string) => {
    const lines = text.split("\n");
    const elements: JSX.Element[] = [];
    let inCodeBlock = false;
    let codeContent = "";
    let codeLang = "";

    lines.forEach((line, i) => {
      if (line.startsWith("```")) {
        if (inCodeBlock) {
          elements.push(
            <pre key={i} className="my-3 rounded-xl bg-[hsl(220,10%,6%)] border border-border p-4 overflow-x-auto">
              <code className="text-sm font-mono text-foreground/90">{codeContent.trim()}</code>
            </pre>
          );
          codeContent = "";
          inCodeBlock = false;
        } else {
          inCodeBlock = true;
          codeLang = line.slice(3);
        }
        return;
      }
      if (inCodeBlock) { codeContent += line + "\n"; return; }
      if (line.startsWith("## ")) {
        elements.push(<h2 key={i} className="text-base font-semibold text-foreground mt-4 mb-2">{line.slice(3)}</h2>);
      } else if (line.startsWith("**") && line.endsWith("**")) {
        elements.push(<p key={i} className="font-semibold text-foreground">{line.slice(2, -2)}</p>);
      } else if (line.startsWith("- ")) {
        elements.push(<li key={i} className="ml-4 text-foreground/85 list-disc">{renderInline(line.slice(2))}</li>);
      } else if (line.startsWith("> ")) {
        elements.push(<blockquote key={i} className="border-l-2 border-primary/40 pl-4 my-2 italic text-muted-foreground">{line.slice(2)}</blockquote>);
      } else if (/^\d+\.\s/.test(line)) {
        elements.push(<li key={i} className="ml-4 text-foreground/85 list-decimal">{renderInline(line.replace(/^\d+\.\s/, ""))}</li>);
      } else if (line.trim() === "") {
        elements.push(<div key={i} className="h-2" />);
      } else {
        elements.push(<p key={i} className="text-foreground/85 leading-relaxed">{renderInline(line)}</p>);
      }
    });
    return elements;
  };

  const renderInline = (text: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("`") && part.endsWith("`")) {
        return <code key={i} className="px-1.5 py-0.5 rounded-md bg-muted text-primary text-sm font-mono">{part.slice(1, -1)}</code>;
      }
      return part;
    });
  };

  return (
    <div
      className={`group flex gap-4 px-4 py-3 animate-fade-in ${isUser ? "justify-end" : ""}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {!isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-xl gold-gradient flex items-center justify-center text-primary-foreground font-bold text-sm mt-1">
          Z
        </div>
      )}
      <div className={`max-w-2xl ${isUser ? "bg-[hsl(var(--message-user))] rounded-2xl rounded-br-md px-4 py-3" : "flex-1"}`}>
        {/* Attachments */}
        {message.attachments && message.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {message.attachments.map((file, i) => (
              <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-secondary/80 border border-border text-xs">
                {file.type.startsWith("image/") ? (
                  file.preview || file.url ? (
                    <img src={file.preview || file.url} alt={file.name} className="w-16 h-16 rounded-lg object-cover" />
                  ) : (
                    <ImageIcon className="h-4 w-4 text-muted-foreground" />
                  )
                ) : (
                  <FileText className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-foreground/80 max-w-[120px] truncate">{file.name}</span>
              </div>
            ))}
          </div>
        )}

        {message.content && <div className="text-sm">{renderContent(message.content)}</div>}

        {isStreaming && message.content.length > 0 && (
          <span className="inline-block w-1.5 h-4 bg-primary ml-0.5 animate-blink rounded-sm" />
        )}

        {!isUser && !isStreaming && message.content.length > 0 && (
          <div className={`flex items-center gap-1 mt-2 transition-opacity duration-200 ${showActions ? "opacity-100" : "opacity-0"}`}>
            <ActionButton icon={Copy} label="Copy" onClick={() => navigator.clipboard.writeText(message.content)} />
            <ActionButton icon={RefreshCw} label="Regenerate" />
            <ActionButton icon={ThumbsUp} label="Good response" />
            <ActionButton icon={ThumbsDown} label="Bad response" />
          </div>
        )}
      </div>
    </div>
  );
}

function ActionButton({ icon: Icon, label, onClick }: { icon: React.ElementType; label: string; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
      aria-label={label}
      title={label}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}
